import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useDerbyStore } from '@/entities/derby';
import { usePredictionsStore } from '@/entities/prediction';
import { useResultsStore } from '@/entities/result';
import { useSessionStore } from '@/entities/session';
import { ROUTES } from '@/shared/config';
import { RADIUS, Squircle } from '@/shared/ui';

import styles from './DerbyNudgeModal.module.scss';

/**
 * «Дерби» началось — приглашение сыграть (узел 777:6711/777:7009).
 * Полноэкранная панель без затемнения фона, по образцу WelcomeModal:
 * шапка, карточки и нижний бар она закрывает собой, а не всплывает
 * поверх них.
 *
 * **Показ решает сервер** — поле `shouldNudge` в состоянии Дерби: оно
 * открыто, игрок его ещё не прошёл и сегодня приглашение не закрывал.
 * То есть не чаще раза в игровой день, пока Дерби идёт. Отметку
 * о закрытии ставит `POST /derby/nudge/seen`: на клиенте её держать
 * негде — в `localStorage` мини-аппа живёт только токен, и приглашение
 * возвращалось бы на другом устройстве.
 *
 * **Очередь модалок.** Приглашение — последнее: приветствие новичку
 * и итоги периодов крупнее и ждать не умеют, а Дерби открыто днями.
 * Две полноэкранные панели одновременно наложились бы друг на друга.
 *
 * «Играть» ведёт в «Прогнозы», к баннеру Дерби, а не сразу в квиз
 * (правка 14.09.2026): там игрок видит, что за Дерби и сколько за него
 * дают, и начинает сам — таймер первого вопроса стартует с открытия.
 *
 * `?derby-nudge=1` в адресе показывает панель принудительно — это вход
 * для сверки вёрстки, отметку на сервере он не ставит.
 */
export function DerbyNudgeModal() {
  const derby = useDerbyStore((state) => state.derby);
  const loadDerby = useDerbyStore((state) => state.load);
  const dismissNudge = useDerbyStore((state) => state.dismissNudge);

  const popup = usePredictionsStore((state) => state.popup);
  const periodResult = useResultsStore((state) => state.pending);
  const isNewUser = useSessionStore((state) => state.isNewUser);

  const [preview] = useState(() =>
    new URLSearchParams(window.location.search).has('derby-nudge'),
  );
  const [dismissed, setDismissed] = useState(false);

  // Тот же запрос делает лента матчей. Дубля не будет: `load` обёрнут
  // в single-flight и уважает окно свежести
  useEffect(() => {
    void loadDerby();
  }, [loadDerby]);

  if (dismissed) return null;
  if (isNewUser || periodResult || popup) return null;
  if (!preview && !derby?.shouldNudge) return null;

  const close = (): void => {
    setDismissed(true);
    // Сверка вёрстки ничего не отмечает: иначе разработчик, открывший
    // панель параметром, погасил бы приглашение живому игроку
    if (!preview) void dismissNudge();
  };

  return (
    <div className={styles.sheet} data-testid="derby-nudge-modal">
      <div className={styles.shapeBg}>
        <img
          className={styles.shapeBgImage}
          src="/images/derby-nudge/shape-bg.svg"
          alt=""
          aria-hidden
        />
      </div>

      <div className={styles.heroFrame}>
        <img
          className={styles.heroImg}
          src="/images/derby-nudge/hero.png"
          alt=""
          aria-hidden
        />
      </div>

      <button
        type="button"
        className={styles.close}
        aria-label="Закрыть"
        onClick={close}
      >
        <img
          className={styles.closeIcon}
          src="/images/matches/close-light.svg"
          alt=""
          aria-hidden
        />
      </button>

      <div className={styles.content}>
        <div className={styles.text}>
          <h2 className={styles.title}>«Дерби» началось!</h2>
          <p className={styles.body}>
            Пока матчи на паузе. Проходи «Дерби» и заработай очки, чтобы стать
            ещё ближе к фрибетам. Для этого жми «Играть» тут или на главном
            экране
          </p>
        </div>

        <Squircle asChild cornerRadius={RADIUS.xl}>
          <Link
            className={styles.cta}
            to={ROUTES.matches}
            onClick={close}
            data-testid="derby-nudge-cta"
          >
            Играть
          </Link>
        </Squircle>
      </div>
    </div>
  );
}
